import React, { useMemo } from 'react';
import {
  Box, Button, ButtonProps, styled, Typography,
} from '@mui/material';
import { DateInputField } from 'common-client/dist/components/DateInputField';

const BackButton = styled(Button, { name: 'BackButton' })<ButtonProps>(() => ({
  borderRadius: '24px',
  textTransform: 'capitalize',
}));

const NextButton = styled(Button, { name: 'NextButton' })<ButtonProps>(() => ({
  borderRadius: '24px',
  textTransform: 'capitalize',
}));

export interface SelectEndDateBoxProps {
  /**
   * Prompt to display
   */
  prompt?: string;
  /**
   * Date
   */
  date: Date | null;
  /**
   * Whether the user has a wallet connected
   */
  isUserConnected: boolean;
  /**
   * Callback for setting the date
   */
  onUpdateDate?: (date: Date | null) => void;
  /**
   * Callback on clicking back button
   */
  onBackClick?: () => void;
  /**
   * Callback on clicking next button
   */
  onNextClick?: () => void;
}

export function SelectEndDateBox({
  prompt,
  date,
  isUserConnected,
  onUpdateDate,
  onBackClick,
  onNextClick,
}: SelectEndDateBoxProps) {
  const today = useMemo(() => new Date().setHours(0, 0, 0, 0), []);

  const dateError = useMemo(() => {
    if (date === null || Number.isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    if (date && date.getTime() <= today) {
      return 'Date must be in the future';
    }
    return null;
  }, [date, today]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexWrap="nowrap"
    >
      <Typography variant="h3" fontWeight={700} fontSize="24px" lineHeight="32px" color="textPrimary" my="8px">
        Select an End Date
      </Typography>
      {prompt && (
        <Typography variant="body1" fontWeight={400} fontSize="16px" lineHeight="24px" color="textSecondary" mb="16px">
          {prompt}
        </Typography>
      )}
      <Typography variant="body1" fontWeight={700} fontSize="16px" lineHeight="24px" color="textPrimary" mb="8px">
        End Date
      </Typography>
      <DateInputField date={date} setDate={onUpdateDate} error={dateError} />
      <Box
        display="flex"
        flexDirection="row"
        flexWrap="nowrap"
        gap="16px"
        mt="24px"
      >
        <BackButton
          color="secondary"
          variant="contained"
          onClick={onBackClick}
          fullWidth
        >
          <Typography variant="body1" fontWeight={600} fontSize="14px" lineHeight="24px">
            Back
          </Typography>
        </BackButton>
        <NextButton
          color="primary"
          variant="contained"
          onClick={onNextClick}
          fullWidth
          disabled={!date || !!dateError}
        >
          <Typography>
            {isUserConnected ? 'Create Auction' : 'Connect'}
          </Typography>
        </NextButton>
      </Box>
    </Box>
  );
}
